import { Box, type SxProps, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import { CATEGORICAL_SERIES, INK, INK_BLACK } from "../../../../theme/tokens";
import { PieSkeleton } from "./PieSkeleton";
import type { InHousePieChartProps, InHousePieDataItem, InHousePieSectorDataItemProps } from "./types";

const getItemColor = (d: InHousePieDataItem, i: number) => d.color ?? CATEGORICAL_SERIES[i % CATEGORICAL_SERIES.length];

const renderActiveShape = ({
	cx,
	cy,
	midAngle,
	innerRadius,
	outerRadius,
	startAngle,
	endAngle,
	fill,
	payload,
	percent,
	value,
	dontRenderInnerLabel,
}: InHousePieSectorDataItemProps) => {
	const currentItem = payload as InHousePieDataItem;
	const RADIAN = Math.PI / 180;
	const sin = Math.sin(-RADIAN * (midAngle ?? 1));
	const cos = Math.cos(-RADIAN * (midAngle ?? 1));
	const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
	const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
	const mx = (cx ?? 0) + ((outerRadius ?? 0) + 20) * cos;
	const my = (cy ?? 0) + ((outerRadius ?? 0) + 20) * sin;
	const ex = mx + (cos >= 0 ? 1 : -1) * 10;
	const ey = my;
	const textAnchor = cos >= 0 ? "start" : "end";

	return (
		<g>
			{!dontRenderInnerLabel && (
				<text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} fontWeight={700} fontSize={12}>
					{payload.name}
				</text>
			)}
			<Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
			<Sector
				cx={cx}
				cy={cy}
				startAngle={startAngle}
				endAngle={endAngle}
				innerRadius={(outerRadius ?? 0) + 6}
				outerRadius={(outerRadius ?? 0) + 10}
				fill={fill}
			/>
			<path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
			<circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
			{currentItem.renderAmountOnHover && (
				<text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill={INK.SECONDARY} fontWeight={700}>{`${value}`}</text>
			)}
			{!currentItem.dontRenderPercentOnHover && (
				<text
					x={ex + (cos >= 0 ? 1 : -1) * 12}
					y={currentItem.renderAmountOnHover ? ey : ey - 12}
					dy={18}
					textAnchor={textAnchor}
					fill={alpha(INK.SECONDARY, 0.5)}
					fontWeight={700}
				>
					{`${((percent ?? 1) * 100).toFixed(0)}%`}
				</text>
			)}
		</g>
	);
};

const LegendContent: FC<{
	data: {
		value: string;
		color: string;
		payload: InHousePieDataItem;
	}[];
	sxOverride?: SxProps;
}> = ({ data, sxOverride }) => {
	const total = data?.reduce((sum: number, entry) => sum + entry.payload.value, 0) || 0;

	return (
		<Box component="ul" sx={{ listStyle: "none", margin: 0, marginLeft: "24px", padding: 0, ...sxOverride }}>
			{data?.map((entry) => {
				const percent = total ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
				return (
					<Box
						component="li"
						key={`item-${entry.value}`}
						sx={{
							display: "flex",
							gap: { xs: "8px", xl: "16px" },
							alignItems: "center",
							marginBottom: 0,
							color: INK.SECONDARY,
							fontWeight: 700,
							fontSize: { xs: "10px", lg: "12px", xl: "14px" },
						}}
					>
						<Box
							component="span"
							sx={{
								width: 10,
								height: 10,
								borderRadius: "50%",
								background: entry.color,
								display: "inline-block",
								flexShrink: 0,
							}}
						/>
						<Box component="span" sx={{ flex: 1, color: alpha(INK.SECONDARY, 0.5), whiteSpace: "nowrap" }}>
							{entry.value}
						</Box>
						<Box component="span" sx={{ opacity: 0.6 }}>
							{percent}%
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};

export const InHousePieChart: FC<InHousePieChartProps> = ({
	isAnimationActive = true,
	defaultIndex = undefined,
	width = "150px",
	height = "150px",
	pieMargin = {
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
	},
	innerRadius = "50%",
	outerRadius = "70%",
	paddingAngle = 2,
	stroke = alpha(INK_BLACK, 0),
	hideLegend = false,
	data,
	dontRenderInnerLabel = false,
	overflowVisible = true,
	legendSxOverride,
}) => {
	const { t } = useTranslation();
	const legendPayload =
		data?.map((d, i) => ({
			value: d.name,
			color: getItemColor(d, i),
			payload: d,
		})) ?? [];

	return (
		<div
			style={{
				width: data ? "fit-content" : `calc(${width} + ${hideLegend ? 0 : 100}px)`,
				height: height,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				position: "relative",
				...(overflowVisible ? { overflow: "visible" } : {}),
			}}
		>
			<div
				style={{
					flex: 1,
					width: width,
					height: "100%",
					position: "relative",
					zIndex: 2,
					...(overflowVisible ? { overflow: "visible" } : {}),
				}}
			>
				{!data ? (
					<PieSkeleton showLegend={!hideLegend} sliceCount={4} />
				) : data.length === 0 || data.every((record) => record.value === 0) ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
						}}
					>
						<Box
							sx={{
								width: `min(100%, ${height})`,
								aspectRatio: "1",
								borderRadius: "50%",
								border: "2px solid",
								borderColor: "text.disabled",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Typography
								sx={{
									color: "text.secondary",
								}}
							>
								{t("No Data")}
							</Typography>
						</Box>
					</Box>
				) : (
					<ResponsiveContainer
						width={"100%"}
						height={"100%"}
						style={{
							...(overflowVisible ? { overflow: "visible" } : {}),
						}}
					>
						<PieChart margin={pieMargin} className={"inhouse-pie-chart"}>
							<Pie
								activeShape={(props: InHousePieSectorDataItemProps) => renderActiveShape({ ...props, dontRenderInnerLabel })}
								data={data}
								cx={"50%"}
								cy={"50%"}
								innerRadius={innerRadius}
								outerRadius={outerRadius}
								dataKey={"value"}
								isAnimationActive={isAnimationActive}
								stroke={stroke}
								paddingAngle={paddingAngle}
								legendType={"circle"}
							>
								{data.map((d, i) => (
									<Cell key={d.name} fill={getItemColor(d, i)} />
								))}
							</Pie>
							<Tooltip content={() => null} defaultIndex={defaultIndex} />
						</PieChart>
					</ResponsiveContainer>
				)}
			</div>
			{!hideLegend && data && (
				<div
					style={{
						zIndex: 1,
						...(overflowVisible ? { overflow: "visible" } : {}),
					}}
				>
					<LegendContent data={legendPayload} sxOverride={legendSxOverride} />
				</div>
			)}
		</div>
	);
};
